import { translate } from '@jsverse/transloco';
import { NavigationItem } from 'vcp-common-ui-lib';

type RouteLink = `/${string}`;

export interface NavigationItemWithVisibility extends Omit<NavigationItem, 'children' | 'routeLink'> {
    visible: boolean;
    privileges: string[];
    routeLink?: RouteLink;
    modulesPaths?: string[];
    children?: NavigationItemWithVisibility[];
}

export const getPrivileges = (): NavigationItemWithVisibility[] => [
    {
        visible: true,
        label: translate('CORE.LEFT_MENU.DASHBOARD'),
        icon: 'dashboard',
        routeLink: '/dashboard',
        privileges: ['INTERACTIVE_USER'],
    },
    {
        visible: false,
        label: '',
        routeLink: '/admin/organization',
        modulesPaths: ['/admin', '/admin/organization'],
        privileges: ['ROLE_ORG_ADMIN'],
    },
    {
        visible: false,
        label: '',
        routeLink: '/admin/organization/details',
        modulesPaths: ['/admin', '/admin/organization'],
        privileges: ['ROLE_ORG_ADMIN'],
    },
    {
        visible: false,
        label: '',
        routeLink: '/admin/organization/settings',
        modulesPaths: ['/admin', '/admin/organization'],
        privileges: ['ROLE_ORG_ADMIN'],
    },
    {
        visible: false,
        label: '',
        routeLink: '/admin/organization/merchant-id',
        modulesPaths: ['/admin', '/admin/organization'],
        privileges: ['ROLE_ORG_ADMIN'],
    },
    {
        visible: false,
        label: '',
        routeLink: '/admin/organization/phone-numbers',
        modulesPaths: ['/admin', '/admin/organization'],
        privileges: ['ROLE_ORG_ADMIN'],
    },
    {
        visible: false,
        label: '',
        routeLink: '/admin/organization/addresses',
        modulesPaths: ['/admin', '/admin/organization'],
        privileges: ['ROLE_ORG_ADMIN'],
    },
    {
        visible: false,
        label: '',
        routeLink: '/admin/organization/users',
        modulesPaths: ['/admin', '/admin/organization'],
        privileges: ['ROLE_ORG_ADMIN'],
    },
    {
        visible: false,
        label: '',
        routeLink: '/admin/organization/edit-user',
        modulesPaths: ['/admin', '/admin/organization'],
        privileges: ['ROLE_ORG_ADMIN'],
    },
    {
        visible: false,
        label: '',
        routeLink: '/admin/organization/edit-address',
        modulesPaths: ['/admin', '/admin/organization'],
        privileges: ['ROLE_ORG_ADMIN'],
    },
    {
        visible: false,
        label: '',
        routeLink: '/admin/organization/create-user',
        modulesPaths: ['/admin', '/admin/organization'],
        privileges: ['ROLE_ORG_ADMIN'],
    },
    {
        visible: true,
        label: translate('CORE.LEFT_MENU.VULNERABILITY_SCANNING.TITLE'),
        icon: 'network_check',
        routeLink: '/scan',
        children: [
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.VULNERABILITY_SCANNING.SCAN_MANAGEMENT'),
                routeLink: '/scan/management/scan-profile/search',
                modulesPaths: ['/scan', '/scan/management'],
                privileges: ['ROLE_SCAN_USER'],
            },
            {
                visible: false,
                label: `Authentication Profile Search`,
                routeLink: '/scan/management/authentication-hub/search',
                modulesPaths: ['/scan', '/scan/management'],
                privileges: ['ROLE_SCAN_USER'],
            },
            {
                visible: false,
                label: 'Authentication Profile Form',
                routeLink: '/scan/management/authentication-hub/form',
                modulesPaths: ['/scan', '/scan/management'],
                privileges: ['ROLE_SCAN_USER'],
            },
            {
                visible: false,
                label: 'Authentication Profile Form',
                routeLink: '/scan/management/authentication-hub/form/:id',
                modulesPaths: ['/scan', '/scan/management'],
                privileges: ['ROLE_SCAN_USER'],
            },
            {
                visible: false,
                label: 'Scan History',
                routeLink: '/scan/history',
                privileges: ['ROLE_SCAN_USER', 'SCAN_INSTANCE_READ'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.VULNERABILITY_SCANNING.SCAN_HISTORY'),
                routeLink: '/scan/history/search',
                privileges: ['ROLE_SCAN_USER', 'SCAN_INSTANCE_READ'],
            },
            {
                visible: false,
                label: 'Scan History Results',
                routeLink: '/scan/history/:id/:module',
                privileges: ['ROLE_SCAN_USER', 'FINDING_READ'],
            },
            {
                visible: false,
                label: 'Scan History Results - Dispute History',
                routeLink: '/scan/history/:id/dispute-history/:disputeId',
                privileges: ['ROLE_SCAN_USER', 'FINDING_READ'],
            },
            {
                visible: false,
                label: 'Scan History Results - Finding Details',
                routeLink: '/scan/history/:id/finding-details/:findingId',
                privileges: ['ROLE_SCAN_USER', 'FINDING_READ'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.VULNERABILITY_SCANNING.SCANNER_APPLIANCES'),
                routeLink: '/scan/scanner/appliances/search',
                privileges: ['ROLE_IVS_SCAN_USER'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.VULNERABILITY_SCANNING.SUPPRESSION_RULES'),
                routeLink: '/scan/suppression-rules/search',
                privileges: ['IVS_SCAN', 'EVS_SCAN'],
                modulesPaths: ['/scan', '/scan/suppression-rules'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/scan/suppression-rules/edit-rule/:id',
                privileges: ['IVS_SCAN', 'EVS_SCAN'],
                modulesPaths: ['/scan', '/scan/suppression-rules'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/scan/suppression-rules/edit-rule',
                privileges: ['IVS_SCAN', 'EVS_SCAN'],
                modulesPaths: ['/scan', '/scan/suppression-rules'],
            },
        ],
        privileges: [
            'ROLE_SCAN_USER',
            'SCAN_INSTANCE_READ',
            'ROLE_IVS_SCAN_USER',
            'IVS_SCAN',
            'ROLE_EVS_SCAN_USER',
            'EVS_SCAN',
        ],
    },
    {
        visible: true,
        label: translate('CORE.LEFT_MENU.ASSESSMENT.TITLE'),
        icon: 'assignment',
        routeLink: '/assessment',
        children: [
            {
                visible: false,
                label: '',
                routeLink: '/assessment/dashboard',
                privileges: ['ASSESSMENT_READ'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.ASSESSMENT.BASIC_DASHBOARD'),
                routeLink: '/assessment/dashboard/basic',
                privileges: ['ASSESSMENT_READ'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.ASSESSMENT.ADVANCE_DASHBOARD'),
                routeLink: '/assessment/dashboard/advanced',
                privileges: ['ASSESSMENT_DASHBOARD_ADVANCED_READ'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.ASSESSMENT.ASSESSMENTS'),
                routeLink: '/assessment/management/search',
                privileges: ['ASSESSMENT_READ'],
            },
            {
                visible: false,
                label: 'Assessment Management',
                routeLink: '/assessment',
                privileges: ['ASSESSMENT_READ'],
                children: [
                    {
                        visible: false,
                        label: 'Assessment Management',
                        routeLink: '/assessment/management',
                        privileges: ['ASSESSMENT_READ'],
                    },
                    {
                        visible: false,
                        label: 'Assessment Management',
                        routeLink: '/assessment/management/:module',
                        privileges: ['ASSESSMENT_READ'],
                    },
                    {
                        visible: false,
                        label: 'Assessment Management',
                        routeLink: '/assessment/management/:module/:id',
                        privileges: ['ASSESSMENT_READ'],
                    },
                    {
                        visible: false,
                        label: 'Assessment Management',
                        routeLink: '/assessment/management/dashboard/modify-task-reviews/:id',
                        privileges: ['ASSESSMENT_ADMIN'],
                    },
                    {
                        visible: false,
                        label: 'Add milestone',
                        routeLink: '/assessment/management/milestone/:id/form',
                        privileges: ['ASSESSMENT_READ'],
                    },
                    {
                        visible: false,
                        label: 'Edit milestone',
                        routeLink: '/assessment/management/milestone/:id/form/:milestoneId',
                        privileges: ['ASSESSMENT_READ'],
                    },
                    {
                        visible: false,
                        label: 'Clients Provide Task Access',
                        routeLink: '/assessment/management/clients/provide-access/:id',
                        privileges: ['ASSESSMENT_READ'],
                    },
                    {
                        visible: false,
                        label: 'Clients Revoke Task Access',
                        routeLink: '/assessment/management/clients/revoke-access/:id',
                        privileges: ['ASSESSMENT_READ'],
                    },
                    {
                        visible: false,
                        label: 'Clients Assign Tasks',
                        routeLink: '/assessment/management/clients/assign-tasks/:id',
                        privileges: ['ASSESSMENT_READ'],
                    },
                    {
                        visible: false,
                        label: 'Clients Unassign Tasks',
                        routeLink: '/assessment/management/clients/unassign-tasks/:id',
                        privileges: ['ASSESSMENT_READ'],
                    },
                    {
                        visible: false,
                        label: 'Assessors Provide Task Access',
                        routeLink: '/assessment/management/assessors/provide-access/:id',
                        privileges: ['ASSESSMENT_READ'],
                    },
                    {
                        visible: false,
                        label: 'Assessors Revoke Task Access',
                        routeLink: '/assessment/management/assessors/revoke-access/:id',
                        privileges: ['ASSESSMENT_READ'],
                    },
                    {
                        visible: false,
                        label: 'Form Component',
                        routeLink: '/assessment/management/components/form/:assessmentId',
                        privileges: ['ASSESSMENT_READ'],
                    },
                    {
                        visible: false,
                        label: 'Form View Component',
                        routeLink: '/assessment/management/components/view/:assessmentId/:componentId',
                        privileges: ['ASSESSMENT_READ'],
                    },
                    {
                        visible: false,
                        label: 'Reorder components',
                        routeLink: '/assessment/management/components/reorder/:id',
                        privileges: ['ASSESSMENT_READ'],
                    },
                    {
                        visible: false,
                        label: 'Reorder reference tasks',
                        routeLink: '/assessment/management/referencetasks/reorder/:id',
                        privileges: ['ASSESSMENT_READ'],
                    },
                ],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.ASSESSMENT.TASKS'),
                routeLink: '/assessment/tasks/search',
                privileges: ['TASK_READ'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/assessment/controls',
                privileges: ['ROLE_CONTROL_ADMIN', 'CONTROL_READ'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.ASSESSMENT.CONTROLS'),
                routeLink: '/assessment/controls/search',
                privileges: ['ROLE_CONTROL_ADMIN'],
            },
            {
                visible: false,
                label: 'Control Form',
                routeLink: '/assessment/controls/form',
                privileges: ['CONTROL_READ'],
            },
            {
                visible: false,
                label: 'Control Form',
                routeLink: '/assessment/controls/form/:module/:id',
                privileges: ['CONTROL_READ'],
            },
            {
                visible: false,
                label: translate('CORE.LEFT_MENU.ASSESSMENT.REFERENCE_TASKS'),
                routeLink: '/assessment/reference-tasks',
                privileges: ['ROLE_REFERENCE_TASK_ADMIN'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.ASSESSMENT.REFERENCE_TASKS'),
                routeLink: '/assessment/reference-tasks/search',
                privileges: ['ROLE_REFERENCE_TASK_ADMIN'],
            },
            {
                visible: false,
                label: 'Reference Task Form',
                routeLink: '/assessment/reference-tasks/form',
                privileges: ['REFERENCE_TASK_READ'],
            },
            {
                visible: false,
                label: 'Reference Task Form',
                routeLink: '/assessment/reference-tasks/form/:module/:id',
                privileges: ['REFERENCE_TASK_READ'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.ASSESSMENT.FRAMEWORKS'),
                routeLink: '/assessment/frameworks/search',
                privileges: ['ROLE_FRAMEWORK_ADMIN'],
            },
            {
                visible: false,
                label: translate('CORE.LEFT_MENU.ASSESSMENT.FRAMEWORKS'),
                routeLink: '/assessment/frameworks',
                privileges: ['ROLE_FRAMEWORK_ADMIN'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/assessment/frameworks/search',
                privileges: ['ROLE_FRAMEWORK_ADMIN', 'FRAMEWORK_READ'],
            },
            {
                visible: false,
                label: 'Framework Create',
                routeLink: '/assessment/frameworks/create',
                privileges: ['FRAMEWORK_READ'],
            },
            {
                visible: false,
                label: 'Framework Edit',
                routeLink: '/assessment/frameworks/edit',
                privileges: ['FRAMEWORK_READ'],
            },
            {
                visible: false,
                label: 'Framework Edit',
                routeLink: '/assessment/frameworks/edit/:id',
                privileges: ['FRAMEWORK_READ'],
            },
            {
                visible: false,
                label: 'Reorder Components',
                routeLink: '/assessment/frameworks/reorder-components/:id',
                privileges: ['FRAMEWORK_READ'],
            },
            {
                visible: false,
                label: 'Reorder reference tasks',
                routeLink: '/assessment/frameworks/reorder-reference-tasks/:id',
                privileges: ['FRAMEWORK_READ'],
            },
            {
                visible: false,
                label: 'Framework Components',
                routeLink: '/assessment/framework-components',
                privileges: ['CONTROL_UPDATE', 'REFERENCE_TASK_UPDATE'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.ASSESSMENT.COMPONENTS'),
                routeLink: '/assessment/framework-components/search',
                privileges: ['CONTROL_UPDATE', 'REFERENCE_TASK_UPDATE'],
            },
            {
                visible: false,
                label: 'Framework Components Form',
                routeLink: '/assessment/framework-components/form',
                privileges: ['CONTROL_READ'],
            },
            {
                visible: false,
                label: 'Framework Components Form',
                routeLink: '/assessment/framework-components/form/:module/:name',
                privileges: ['ASSESSMENT_READ', 'CONTROL_UPDATE'],
            },
            {
                visible: false,
                label: 'Reorder Task',
                routeLink: '/assessment/framework-components/reorder-reference-tasks/:id',
                privileges: ['ASSESSMENT_READ'],
            },
        ],
        privileges: [
            'ASSESSMENT_READ',
            'TASK_READ',
            'ROLE_CONTROL_ADMIN',
            'ROLE_REFERENCE_TASK_ADMIN',
            'ROLE_FRAMEWORK_ADMIN',
            'ASSESSMENT_DASHBOARD_ADVANCED_READ',
            'CONTROL_UPDATE',
            'REFERENCE_TASK_UPDATE',
        ],
    },
    {
        visible: true,
        label: translate('CORE.LEFT_MENU.MSS'),
        icon: 'security',
        routeLink: '/mss',
        privileges: ['MSS_DASHBOARD_VIEW'],
        children: [
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.DASHBOARD'),
                routeLink: '/mss/dashboard',
                privileges: ['MSS_DASHBOARD_VIEW'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.EVENT_ARCHIVE'),
                routeLink: '/mss/event-archive',
                privileges: ['EVENTARCHIVE_VIEWER'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.TICKETS'),
                routeLink: '/mss/ticket',
                privileges: ['VC_TICKETING_VIEW'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/mss/ticket/search',
                privileges: ['VC_TICKETING_VIEW'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/mss/ticket/add/new',
                privileges: ['VC_TICKETING_VIEW'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/mss/ticket/detail',
                privileges: ['VC_TICKETING_VIEW'],
            },
        ],
    },
    {
        visible: false,
        label: '',
        icon: 'security',
        privileges: ['MSS_DASHBOARD_VIEW'],
        children: [
            {
                visible: false,
                label: '',
                routeLink: '/mss/:module',
                privileges: ['MSS_DASHBOARD_VIEW'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/mss/managed-connectivity',
                privileges: ['MSS_CONNECTIVITY_VIEW, MSS_MANAGED_CONNECTIVITY_VIEW'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/mss/site-availability',
                privileges: ['MSS_DEMO_USER'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/mss/site-detail/:tabName/:id/:siteId',
                privileges: ['MSS_DEMO_USER'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/mss/site-availability',
                privileges: ['MSS_SITE_AVAILABILITY_VIEW'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/mss/lte-usage',
                privileges: ['ROLE_MSS_VIEWER'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/mss/site-all',
                privileges: ['MSS_SITE_AVAILABILITY_VIEW'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/mss/site-detail/:index/:id/:siteId',
                privileges: ['MSS_SITE_VIEW'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/mss/ids-report',
                privileges: ['MSS_QBR_REPORT_VIEW'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/mss/ids-report/detail/:signature/:severity',
                privileges: ['MSS_QBR_REPORT_VIEW'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/mss/ids-report/detail/site/:orgId/:orgName',
                privileges: ['MSS_QBR_REPORT_VIEW'],
            },
        ],
    },
    {
        visible: true,
        label: translate('CORE.LEFT_MENU.MSS.MSS_SUPPORT'),
        icon: 'security',
        routeLink: '/support-mss',
        privileges: ['ISP_PROVIDERS_VIEW', 'ROLE_MSS_SUPPORT_USER'],
        children: [
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.MSS.ISP_PROVIDER'),
                routeLink: '/support-mss/isp-provider',
                privileges: ['ISP_PROVIDERS_VIEW'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/support-mss/isp-provider/detail/:providerId',
                privileges: ['ISP_PROVIDERS_VIEW'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.MSS.LTE_ADMIN'),
                routeLink: '/support-mss/lte-admin',
                privileges: ['LTE_ADMINISTRATION_VIEW'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.MSS.FIREWALL_DEVICES'),
                routeLink: '/support-mss/firewall-devices/all',
                privileges: ['ROLE_MSS_SUPPORT_USER'],
            },
            {
                visible: false,
                label: 'Recently down',
                routeLink: '/support-mss/firewall-devices/recently-down',
                privileges: ['ROLE_MSS_SUPPORT_USER'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.MSS.UNKNOWN_DEVICES'),
                routeLink: '/support-mss/unknown-devices',
                privileges: ['ROLE_MSS_SUPPORT_USER'],
            },
        ],
    },
    {
        visible: true,
        label: translate('CORE.LEFT_MENU.MARKETPLACE'),
        icon: 'shopping_cart',
        modulesPaths: ['/marketplace'],
        privileges: ['WALLET_READ', 'STORE_READ', 'PAYMENT_GATEWAY_READ'],
        children: [
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.WALLET'),
                modulesPaths: ['/marketplace/wallet'],
                routeLink: '/marketplace/wallet/view',
                privileges: ['WALLET_READ'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.CATALOG'),
                modulesPaths: ['/marketplace/catalog'],
                routeLink: '/marketplace/catalog/view',
                privileges: ['STORE_READ'],
            },
            {
                visible: false,
                label: 'Marketplace Checkout',
                modulesPaths: ['/marketplace/catalog'],
                routeLink: '/marketplace/catalog/checkout',
                privileges: ['STORE_PURCHASE'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.ORDERS'),
                modulesPaths: ['/marketplace/orders'],
                routeLink: '/marketplace/orders/search',
                privileges: ['STORE_READ'],
            },
            {
                visible: false,
                label: 'Order View',
                modulesPaths: ['/marketplace/orders'],
                routeLink: '/marketplace/orders/:module/:orderId',
                privileges: ['STORE_READ'],
            },
        ],
    },

    // Endpoints
    {
        visible: true,
        label: translate('CORE.LEFT_MENU.ENDPOINTS'),
        icon: 'desktop_windows',
        routeLink: '/endpoints',
        modulesPaths: ['/endpoints'],
        privileges: ['ENDPOINT_READ'],
    },
    {
        visible: false,
        label: translate('CORE.LEFT_MENU.ENDPOINTS'),
        icon: 'desktop_windows',
        routeLink: '/endpoints/management/dashboard',
        modulesPaths: ['/endpoints'],
        privileges: ['ENDPOINT_READ'],
    },
    {
        visible: false,
        label: 'Endpoint Management',
        routeLink: '/endpoints/management/:module',
        privileges: ['ENDPOINT_READ'],
    },
    {
        visible: false,
        label: 'Endpoint Software Inventory Detail',
        routeLink: '/endpoints/management/software-inventory/detail/:name/:version',
        privileges: ['ENDPOINT_READ'],
    },
    {
        visible: false,
        label: 'Endpoint Device Detail',
        routeLink: '/endpoints/device/detail/:endpointId',
        privileges: ['ENDPOINT_READ'],
    },

    // SecureEdge
    {
        visible: true,
        label: 'SecureEdge',
        icon: 'lock',
        routeLink: '/secure-edge',
        privileges: ['ROLE_SECUREEDGE_VIEWER'],
    },

    // Findings
    {
        visible: true,
        label: translate('CORE.LEFT_MENU.FINDINGS.TITLE'),
        icon: 'find_in_page',
        modulesPaths: ['/finding'],
        routeLink: '/finding/search',
        privileges: ['INTERACTIVE_USER', 'FINDING_READ', 'ENDPOINT_FINDING_UI_READ'],
    },
    {
        visible: false,
        label: 'Finding Device',
        routeLink: '/finding/device/:orgId',
        privileges: ['INTERACTIVE_USER', 'FINDING_READ', 'ENDPOINT_FINDING_UI_READ'],
    },
    {
        visible: false,
        label: 'Finding View',
        routeLink: '/finding/view/:module/:id',
        privileges: ['FINDING_READ'],
    },
    {
        visible: false,
        label: 'Finding Form',
        routeLink: '/finding/form/:module/:id',
        privileges: ['FINDING_READ'],
    },

    // Events
    {
        visible: true,
        label: translate('CORE.LEFT_MENU.EVENTS'),
        icon: 'event',
        routeLink: '/event/search',
        modulesPaths: ['/event'],
        privileges: ['INTERACTIVE_USER', 'EVENT_READ'],
        children: [
            {
                visible: false,
                label: 'Event Detail',
                routeLink: '/event/view/:module/:id',
                privileges: ['EVENT_READ'],
            },
        ],
    },
    {
        visible: false,
        label: 'Event Device',
        routeLink: '/event/device/:orgId',
        privileges: ['INTERACTIVE_USER', 'EVENT_READ'],
    },

    // Assets
    {
        visible: true,
        label: translate('CORE.LEFT_MENU.ASSETS'),
        routeLink: '/asset/search',
        icon: 'flip_to_front',
        modulesPaths: ['/asset'],
        privileges: ['ASSET_READ', 'ASSET_MENU'],
        children: [
            {
                visible: false,
                label: 'Asset Form Detail',
                routeLink: '/asset/form/:module/:id',
                privileges: ['ASSET_READ'],
            },
            {
                visible: false,
                label: 'Asset Form',
                routeLink: '/asset/form',
                privileges: ['ASSET_READ'],
            },
            {
                visible: false,
                label: 'Asset View Detail',
                routeLink: '/asset/view/:module',
                privileges: ['ASSET_READ'],
            },
        ],
    },

    // File Browser
    {
        visible: false,
        label: translate('CORE.LEFT_MENU.FILE_BROWSER'),
        icon: 'file_copy',
        routeLink: '/file-browser',
        privileges: ['FILE_LOCKER_UI_READ'],
    },
    {
        visible: true,
        label: translate('CORE.LEFT_MENU.FILE_BROWSER'),
        icon: 'file_copy',
        routeLink: '/file-browser/search',
        privileges: ['FILE_LOCKER_UI_READ'],
    },

    // Admin
    {
        visible: true,
        label: translate('CORE.LEFT_MENU.ADMIN.TITLE'),
        icon: 'supervisor_account',
        routeLink: '/admin',
        privileges: [
            'ORG_ADMIN',
            'USER_ADMIN',
            'ROLE_ROLE_ADMIN',
            'ROLE_PRIVILEGE_ADMIN',
            'PTAG_CREATE',
            'PTAG_DELETE',
            'PTAG_UPDATE',
            'ROLE_VULN_SCANNER_ADMIN',
            'ROLE_DISPUTE_ADMIN',
            'ROLE_USER_ADMIN_USERS',
            'PRODUCT_FAMILY_INTERNAL',
            'ADMIN_ORG_ENDPOINT_READ',
        ],
        children: [
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.ADMIN.ORGANIZATIONS'),
                routeLink: '/admin/org/search',
                modulesPaths: ['/admin', '/admin/org'],
                privileges: ['ORG_ADMIN'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.ADMIN.USERS'),
                routeLink: '/admin/user/management/search',
                modulesPaths: ['/admin', '/admin/user/management'],
                privileges: ['USER_ADMIN'],
            },
            {
                visible: false,
                label: translate('CORE.LEFT_MENU.ADMIN.USERS'),
                routeLink: '/admin/user/management/search/:orgId',
                modulesPaths: ['/admin', '/admin/user/management'],
                privileges: ['USER_READ'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.ADMIN.USERS'),
                routeLink: '/admin/user/search',
                privileges: ['ROLE_USER_ADMIN_USERS'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.ORDERS'),
                routeLink: '/admin/orders',
                privileges: ['ROLE_WALLET_ADMIN'],
            },
            {
                visible: false,
                label: 'Admin Order View',
                routeLink: '/admin/orders/:module/:orderId',
                privileges: ['ROLE_WALLET_ADMIN'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.ADMIN.ROLES_AND_PRIVILEGES'),
                routeLink: '/admin/roles-and-privileges',
                modulesPaths: ['/admin', '/roles-and-privileges'],
                privileges: ['ROLE_ROLE_ADMIN', 'ROLE_PRIVILEGE_ADMIN'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/admin/roles-and-privileges/roles/:name',
                modulesPaths: ['/admin', '/roles-and-privileges'],
                privileges: ['ROLE_ROLE_ADMIN'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/admin/roles-and-privileges/roles',
                modulesPaths: ['/admin', '/roles-and-privileges'],
                privileges: ['ROLE_ROLE_ADMIN'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/admin/roles-and-privileges/role-edit/:role',
                modulesPaths: ['/admin', '/roles-and-privileges'],
                privileges: ['ROLE_ROLE_ADMIN'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/admin/roles-and-privileges/role-edit',
                modulesPaths: ['/admin', '/roles-and-privileges'],
                privileges: ['ROLE_ROLE_ADMIN'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/admin/roles-and-privileges/role-hierarchy',
                modulesPaths: ['/admin', '/roles-and-privileges'],
                privileges: ['ROLE_ROLE_ADMIN'],
                children: [
                    {
                        visible: false,
                        label: '',
                        routeLink: '/admin/roles-and-privileges/role-hierarchy/edit/:role',
                        modulesPaths: ['/admin', '/roles-and-privileges'],
                        privileges: ['ROLE_ROLE_ADMIN'],
                    },
                ],
            },
            {
                visible: false,
                label: '',
                routeLink: '/admin/roles-and-privileges/privileges',
                modulesPaths: ['/admin', '/roles-and-privileges'],
                privileges: ['ROLE_PRIVILEGE_ADMIN'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/admin/roles-and-privileges/privilege-edit/:name',
                modulesPaths: ['/admin', '/roles-and-privileges'],
                privileges: ['ROLE_PRIVILEGE_ADMIN'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/admin/roles-and-privileges/privilege-edit',
                modulesPaths: ['/admin', '/roles-and-privileges'],
                privileges: ['ROLE_PRIVILEGE_ADMIN'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.ADMIN.PRIVATE_TAGS'),
                routeLink: '/admin/private/tag/search',
                privileges: ['PTAG_CREATE', 'PTAG_DELETE', 'PTAG_UPDATE'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.SCANNING'),
                routeLink: '/admin/scanning/scan/search',
                modulesPaths: ['/admin', '/admin/scanning'],
                privileges: ['ROLE_VULN_SCANNER_ADMIN'],
            },
            {
                visible: false,
                label: 'Scan counts report',
                routeLink: '/admin/scanning/scan-counts-report',
                modulesPaths: ['/admin', '/admin/scanning'],
                privileges: ['ROLE_VULN_SCANNER_ADMIN'],
            },
            {
                visible: false,
                label: 'Public IP exclusion for IVS',
                routeLink: '/admin/scanning/ivs-ip-exclusions',
                modulesPaths: ['/admin', '/admin/scanning'],
                privileges: ['ROLE_VULN_SCANNER_ADMIN'],
            },
            {
                visible: false,
                label: 'Scanner Search',
                routeLink: '/admin/scanning/scanner/search',
                modulesPaths: ['/admin', '/admin/scanning'],
                privileges: ['ROLE_VULN_SCANNER_ADMIN'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.ADMIN.ENDPOINT'),
                routeLink: '/admin/endpoint',
                modulesPaths: ['/admin', '/admin/endpoint'],
                privileges: ['ADMIN_ORG_ENDPOINT_READ'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.DISPUTE'),
                routeLink: '/admin/disputes/search',
                modulesPaths: ['/admin', '/admin/disputes'],
                privileges: ['ROLE_DISPUTE_ADMIN'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/admin/disputes/finding-details/:id',
                modulesPaths: ['/admin', '/admin/disputes'],
                privileges: ['ROLE_DISPUTE_ADMIN'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.ADMIN.MSS_PARENT_ACCOUNT'),
                routeLink: '/admin/mss/parent-accounts',
                modulesPaths: ['/admin', '/admin/mss'],
                privileges: ['MSS_PARENT_ACCOUNT_ADMIN'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/admin/mss',
                modulesPaths: ['/admin', '/mss'],
                privileges: ['MSS_PARENT_ACCOUNT_ADMIN'],
                children: [
                    {
                        visible: false,
                        label: '',
                        routeLink: '/admin/mss/:module',
                        privileges: ['MSS_PARENT_ACCOUNT_ADMIN'],
                    },
                    {
                        visible: false,
                        label: '',
                        routeLink: '/admin/mss/parent-accounts/form',
                        privileges: ['MSS_PARENT_ACCOUNT_ADMIN'],
                    },
                    {
                        visible: false,
                        label: '',
                        routeLink: '/admin/mss/parent-accounts/form/:id',
                        privileges: ['MSS_PARENT_ACCOUNT_ADMIN'],
                    },
                    {
                        visible: false,
                        label: '',
                        routeLink: '/admin/mss/parent-accounts/view/:id',
                        privileges: ['MSS_PARENT_ACCOUNT_ADMIN'],
                    },
                ],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.ADMIN.PRODUCT_FAMILIES_LINES'),
                routeLink: '/admin/pfpl/list',
                modulesPaths: ['/admin', '/admin/pfpl'],
                privileges: ['PRODUCT_FAMILY_INTERNAL', 'PRODUCT_FAMILY_ADMIN'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/admin/pfpl/edit-product/:id',
                modulesPaths: ['/admin', '/admin/pfpl'],
                privileges: ['PRODUCT_FAMILY_ADMIN'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/admin/pfpl/edit-product',
                modulesPaths: ['/admin', '/admin/pfpl'],
                privileges: ['PRODUCT_FAMILY_ADMIN'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/admin/pfpl/edit-line/:familyId',
                modulesPaths: ['/admin', '/admin/pfpl'],
                privileges: ['PRODUCT_FAMILY_ADMIN'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/admin/pfpl/edit-line/:familyId/:id',
                modulesPaths: ['/admin', '/admin/pfpl'],
                privileges: ['PRODUCT_FAMILY_ADMIN'],
            },
            {
                visible: false,
                label: '',
                routeLink: '/admin/pfpl/edit-line',
                modulesPaths: ['/admin', '/admin/pfpl'],
                privileges: ['PRODUCT_FAMILY_ADMIN'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.ADMIN.PROVISIONING'),
                routeLink: '/admin/provisioning',
                privileges: ['ROLE_MSS_SUPPORT_USER'],
            },
        ],
    },
    {
        visible: false,
        label: translate('CORE.LEFT_MENU.DEVICES_MANAGEMENT.TITLE'),
        icon: 'rss_feed',
        privileges: [
            'DEVICE_MANAGEMENT_TEMPLATES_READ',
            'DEVICE_MANAGEMENT_SITES_READ',
            'DEVICE_MANAGEMENT_DEVICES_READ',
        ],
    },

    // Security Operations
    {
        visible: true,
        label: translate('CORE.LEFT_MENU.SECURITY_OPERATIONS.TITLE'),
        icon: 'fingerprint',
        privileges: ['DEMO_USER'],
        routeLink: '/security-operations',
        children: [
            {
                visible: false,
                label: translate('CORE.LEFT_MENU.SECURITY_OPERATIONS.FILTERS'),
                routeLink: '/security-operations/filter',
                privileges: ['DEMO_USER'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.SECURITY_OPERATIONS.FILTERS'),
                routeLink: '/security-operations/filter/search',
                privileges: ['DEMO_USER'],
            },
            {
                visible: false,
                label: translate('CORE.LEFT_MENU.SECURITY_OPERATIONS.CORRELATIONS'),
                routeLink: '/security-operations/correlation',
                privileges: ['DEMO_USER'],
            },
            {
                visible: false,
                label: translate('CORE.LEFT_MENU.SECURITY_OPERATIONS.CORRELATIONS'),
                routeLink: '/security-operations/correlation/form',
                privileges: ['DEMO_USER'],
            },
            {
                visible: false,
                label: translate('CORE.LEFT_MENU.SECURITY_OPERATIONS.CORRELATIONS'),
                routeLink: '/security-operations/correlation/form/:id',
                privileges: ['DEMO_USER'],
            },
            {
                visible: false,
                label: 'Filter Form',
                routeLink: '/security-operations/filter/form',
                privileges: ['DEMO_USER'],
            },
            {
                visible: false,
                label: 'Filter Form',
                routeLink: '/security-operations/filter/form/:id',
                privileges: ['DEMO_USER'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.SECURITY_OPERATIONS.CORRELATIONS'),
                routeLink: '/security-operations/correlation/search',
                privileges: ['DEMO_USER'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.SECURITY_OPERATIONS.ENRICHER'),
                routeLink: '/security-operations/enricher/search',
                privileges: ['DEMO_USER'],
            },
            {
                visible: true,
                label: translate('CORE.LEFT_MENU.SECURITY_OPERATIONS.TEMPLATES'),
                routeLink: '/security-operations/template/search',
                privileges: ['DEMO_USER'],
            },
        ],
    },

    // Auth guard permission check routes (menu boolean will be false)
    {
        visible: false,
        label: 'Organization Review',
        routeLink: '/org/review',
        privileges: ['ORG_READ'],
    },
    {
        visible: false,
        label: 'Organization Management',
        routeLink: '/admin',
        privileges: ['ORG_READ', 'ORG_ADMIN'],
        children: [
            {
                visible: false,
                label: 'Org Management',
                routeLink: '/admin/org/management/:module',
                modulesPaths: ['/admin', '/admin/org'],
                privileges: ['ORG_READ'],
            },
            {
                visible: false,
                label: 'Org Management',
                routeLink: '/admin/org/management/:module/:id',
                modulesPaths: ['/admin', '/admin/org'],
                privileges: ['ORG_READ'],
            },
            {
                visible: false,
                label: 'Org Endpoint Provisioning Create',
                routeLink: '/admin/org/management/endpoint-provisioning/create/:orgId',
                modulesPaths: ['/admin', '/admin/org'],
                privileges: ['ORG_ADMIN'],
            },
        ],
    },
    {
        visible: false,
        label: 'Finding Search',
        routeLink: '/finding/search',
        privileges: ['FINDING_READ'],
    },
    {
        visible: false,
        label: 'User Management',
        routeLink: '/admin/user/management',
        privileges: ['USER_CREATE', 'USER_UPDATE', 'USER_READ'],
    },
    {
        visible: false,
        label: 'User Management Details',
        routeLink: '/admin/user/management/details/:orgId',
        modulesPaths: ['/admin', '/admin/user/management'],
        privileges: ['USER_CREATE', 'USER_UPDATE', 'USER_READ'],
    },
    {
        visible: false,
        label: 'User Management Details',
        routeLink: '/admin/user/management/details',
        modulesPaths: ['/admin', '/admin/user/management'],
        privileges: ['USER_CREATE', 'USER_UPDATE', 'USER_READ'],
    },
    {
        visible: false,
        label: 'User Management Create User',
        routeLink: '/admin/user/management/create/:orgId',
        modulesPaths: ['/admin', '/admin/user/management'],
        privileges: ['USER_CREATE'],
    },
    {
        visible: false,
        label: 'User Management Create User',
        routeLink: '/admin/user/management/create',
        modulesPaths: ['/admin', '/admin/user/management'],
        privileges: ['USER_CREATE'],
    },
    {
        visible: false,
        label: 'Role Form',
        routeLink: '/admin/role/form',
        privileges: ['ROLE_READ'],
    },
    {
        visible: false,
        label: 'Role Form',
        routeLink: '/admin/role/form/:id',
        privileges: ['ROLE_READ'],
    },
    {
        visible: false,
        label: 'Privilege Form',
        routeLink: '/admin/privilege/form',
        privileges: ['PRIVILEGE_READ'],
    },
    {
        visible: false,
        label: 'Privilege Form',
        routeLink: '/admin/privilege/form/:id',
        privileges: ['PRIVILEGE_READ'],
    },
    {
        visible: false,
        label: 'Finding Form',
        routeLink: '/finding/form/:module',
        privileges: ['FINDING_READ'],
    },
    {
        visible: false,
        label: 'Merchant Scan Results Search',
        routeLink: '/merchant/scan/results/search',
        privileges: ['DEMO_USER'],
    },
    {
        visible: false,
        label: 'Endpoint Configuration Form',
        routeLink: '/endpoint/configuration/form',
        privileges: ['ENDPOINT_USER'],
    },
    {
        visible: false,
        label: 'Endpoint Configuration Form',
        routeLink: '/endpoint/configuration/form/:id',
        privileges: ['ENDPOINT_USER'],
    },
    {
        visible: false,
        label: 'Enricher Form',
        routeLink: '/security-operations/enricher/form',
        privileges: ['DEMO_USER'],
    },
    {
        visible: false,
        label: 'Enricher Form',
        routeLink: '/security-operations/enricher/form/:id',
        privileges: ['DEMO_USER'],
    },
    {
        visible: false,
        label: 'Task Management',
        routeLink: '/assessment/tasks',
        privileges: ['TASK_READ'],
        children: [
            {
                visible: false,
                label: 'Task Management',
                routeLink: '/assessment/tasks/search/:assessmentId/:id',
                privileges: ['TASK_READ'],
            },
            {
                visible: false,
                label: 'Task Management',
                routeLink: '/assessment/tasks/search/:assessmentId/:id/:module',
                privileges: ['TASK_READ'],
            },
            {
                visible: false,
                label: 'Task Delegation',
                routeLink: '/assessment/tasks/delegation/:assessmentId',
                privileges: ['ASSESSOR_PRIVILEGE', 'ROLE_ASSESSMENT_USER'],
            },
            {
                visible: false,
                label: 'Task Unassign',
                routeLink: '/assessment/tasks/unassign/:assessmentId',
                privileges: ['ASSESSOR_PRIVILEGE', 'ROLE_ASSESSMENT_USER'],
            },
            {
                visible: false,
                label: 'Task Bulk Actions',
                routeLink: '/assessment/tasks/bulk-actions/:assessmentId',
                privileges: ['ASSESSOR_PRIVILEGE', 'ROLE_ASSESSMENT_USER'],
            },
            {
                visible: false,
                label: 'Task Bulk Action summary',
                routeLink: '/assessment/tasks/bulk-action-summary/:assessmentId/:taskBulkActionType',
                privileges: ['ASSESSOR_PRIVILEGE', 'ROLE_ASSESSMENT_USER'],
            },
            {
                visible: false,
                label: 'Task Management',
                routeLink: '/assessment/tasks/management/:assessmentId',
                privileges: ['TASK_READ'],
            },
            {
                visible: false,
                label: 'Task Management',
                routeLink: '/assessment/tasks/management/:assessmentId/:id',
                privileges: ['TASK_READ'],
            },
            {
                visible: false,
                label: 'Task Management',
                routeLink: '/assessment/tasks/management/:assessmentId/:id/:module',
                privileges: ['TASK_READ'],
            },
            {
                visible: false,
                label: 'Ad hoc task form',
                routeLink: '/assessment/tasks/management/ad-hoc/form/:assessmentId/:id',
                privileges: ['ASSESSOR_PRIVILEGE'],
            },
            {
                visible: false,
                label: 'Ad hoc task response form',
                routeLink: '/assessment/tasks/management/ad-hoc-response/form/:assessmentId/:id',
                privileges: ['ASSESSOR_PRIVILEGE'],
            },
        ],
    },
    {
        visible: false,
        label: 'Scan Profile Form',
        routeLink: '/scan/management/scan-profile/form',
        privileges: ['ROLE_SCAN_USER'],
    },
    {
        visible: false,
        label: 'Scan Profile Form',
        routeLink: '/scan/management/scan-profile/form/:module/:id',
        privileges: ['ROLE_SCAN_USER'],
    },
    {
        visible: false,
        label: 'Scan Profile Search',
        routeLink: '/scan/management/scan-profile/search',
        privileges: ['ROLE_SCAN_USER'],
    },
    {
        visible: false,
        label: 'Scan Profile Form',
        routeLink: '/scan/management/scan-profile/form',
        privileges: ['ROLE_SCAN_USER'],
    },
    {
        visible: false,
        label: 'Endpoint Management',
        routeLink: '/endpoints/management/:module',
        privileges: ['ENDPOINT_READ'],
    },
    {
        visible: false,
        label: 'Endpoint Software Inventory Detail',
        routeLink: '/endpoints/management/software-inventory/detail/:name/:version',
        privileges: ['ENDPOINT_READ'],
    },
    {
        visible: false,
        label: 'Endpoint Device Detail',
        routeLink: '/endpoints/device/detail/:endpointId',
        privileges: ['ENDPOINT_READ'],
    },
    {
        visible: false,
        label: 'MSS',
        routeLink: '/mss/site/detail/:tabName/:id/:siteId',
        privileges: ['MSS_DEMO_USER'],
    },
];
